/**
 * Which invoice world is the user looking at? (Rechnungen 2.0 — R2)
 *
 * An organization either creates its invoices in Vantio or in Bexio, and that
 * one stored intention decides both which world is CURRENT and what the other
 * one may honestly be called:
 *
 *   provider "bexio"   →  Bexio aktuell  |  Vantio Historie
 *   provider "native"  →  Vantio aktuell        (there is no second world)
 *
 * SOURCE AND LABEL RESOLVE SEPARATELY, and that split is the whole design.
 * `?source=native` means the same CONTENT under both providers — the invoices
 * Vantio itself raised — but a different NAME: current under `native`, history
 * under `bexio`. So the content may appear as soon as it is asked for, while the
 * name waits for the provider. Resolving them together would force a choice
 * between a slow page and a wrong word, and the wrong word is not recoverable:
 * a reader who saw "Vantio Historie" for 200ms has been told something false
 * about their business.
 *
 * THE DEFAULT COMES FROM THE PROVIDER AND NOTHING ELSE. Not from
 * `nativeCreationEnabled` — that boolean is deliberately fail-closed to `false`
 * while loading and after a failed read (see `useInvoiceCapabilities`), which is
 * right for hiding a create button and wrong here: it would collapse "this org
 * bills in Bexio" and "we do not know yet" into one value, and reading `false`
 * as "so, Bexio" would assert a current world nobody confirmed. Not from the
 * Bexio connection either, and not from how many mirrored rows exist or how
 * fresh they are. A provider is an INTENTION; a connection is a CAPABILITY; a
 * row count is a FACT ABOUT DATA. This module reads only the first. A broken
 * Bexio token must never make Vantio's archive look like the current books.
 *
 * WHAT IT NEVER DOES. It writes one search parameter. It performs no request of
 * its own, touches no storage, and has no path to the provider column — a click
 * on a tab changes what you are LOOKING AT, never what the organization IS. The
 * only writer of that column is a backend endpoint with its own pre-checks and
 * audit row, and the frontend has never called it.
 */
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** The only two worlds. There is no combined value, and there never will be. */
export const INVOICE_SOURCES = ["bexio", "native"];

/** The search parameter the choice travels in. */
export const INVOICE_SOURCE_PARAM = "source";

/**
 * A raw `?source=` value, or `null` if it is not one of the two.
 *
 * EXACT AND CASE-SENSITIVE, with no trimming and no aliases. A URL is written by
 * a person or by our own code; neither produces `" native "` or `"BEXIO"` by
 * accident, so accepting them would only mean guessing at intent. `"all"` is
 * rejected like any other unknown word, because a combined view is not a value
 * this product has — the two worlds were never simultaneously in force, so a
 * union of them counts nothing real.
 */
export function normalizeInvoiceSource(raw) {
  return INVOICE_SOURCES.includes(raw) ? raw : null;
}

/** The provider, but only when it has actually been confirmed. */
function confirmedProviderOf({ loading, error, provider }) {
  if (loading || error) return null;
  return provider === "bexio" || provider === "native" ? provider : null;
}

/**
 * Which world to show.
 *
 * Returns `{ source, ignoredRequest }` where `source` is `"bexio"`, `"native"`
 * or `null` — `null` meaning "not yet known", never "empty". A caller must not
 * render either world as current while it is `null`.
 *
 * The asymmetry between the two requests is deliberate:
 *
 *   ?source=native  resolves IMMEDIATELY, under every provider and while the
 *                   capability read is still in flight or has failed. It names
 *                   content that exists either way, so honouring it asserts
 *                   nothing that could turn out false.
 *
 *   ?source=bexio   WAITS for a confirmed provider. It is a claim about which
 *                   world is current, and under `native` that claim is simply
 *                   untrue — so there it is dropped in favour of the org's own
 *                   world, and `ignoredRequest` says so, so the page can explain
 *                   itself rather than appear to have misread the link.
 */
export function resolveInvoiceSource({
  requestedSource,
  loading,
  error,
  provider,
}) {
  const requested = normalizeInvoiceSource(requestedSource);
  const confirmed = confirmedProviderOf({ loading, error, provider });

  if (requested === "native") {
    return { source: "native", ignoredRequest: false };
  }

  if (requested === "bexio") {
    if (confirmed === "bexio") return { source: "bexio", ignoredRequest: false };
    // Not an error and not a redirect: the link is fine, it just does not
    // describe this organization. The page says so; the URL stays as written.
    if (confirmed === "native") return { source: "native", ignoredRequest: true };
    return { source: null, ignoredRequest: false };
  }

  // No usable request — including `all`, `""` and anything misspelled. Fall back
  // to the organization's own world, once we actually know it.
  return { source: confirmed, ignoredRequest: false };
}

/**
 * What to CALL each world.
 *
 * `active` is the name of the world currently shown; `bexio` and `native` are
 * the names its switch may offer. A `null` means "there is no honest name for
 * this here" — either the world does not exist for this organization, or the
 * provider has not been confirmed and any ranking between the two would be
 * invented.
 *
 * While the provider is unknown the native world is called plain "Vantio": true
 * under both providers, and carrying neither "aktuell" nor "Historie", which are
 * exactly the two claims that require knowing the provider.
 */
export function resolveSourceLabels({ source, provider }) {
  if (provider === "bexio") {
    const labels = { bexio: "Bexio aktuell", native: "Vantio Historie" };
    return { ...labels, active: source ? labels[source] : null };
  }

  if (provider === "native") {
    // No Bexio label at all — not a disabled one, not a greyed one. Naming a
    // world this organization does not have would invite the reader to look for
    // it.
    const labels = { bexio: null, native: "Vantio aktuell" };
    return { ...labels, active: source === "native" ? labels.native : null };
  }

  const labels = { bexio: null, native: source === "native" ? "Vantio" : null };
  return { ...labels, active: labels.native };
}

/**
 * The page-level hook. One source of truth for the invoice world on screen.
 *
 * `provider` is the CONFIRMED provider or `null`; components branch on it to
 * decide whether there are two worlds, one, or not yet an answer. `isResolving`
 * is true only while the capability read is in flight — an error is not
 * "resolving", it is a settled failure with a `retry`.
 *
 * THE CAPABILITY READ IS THE CALLER'S, NOT THIS HOOK'S. It used to be performed
 * here, and the page performed it a second time for its creation CTA: two
 * identical GETs on every mount, and two independent copies of the same loading
 * and error state, free to disagree for as long as one was in flight. The page
 * reads once and hands the four fields down.
 *
 * IT IS HANDED FOUR FIELDS AND DELIBERATELY NOT A FIFTH.
 * `nativeCreationEnabled` stays with the caller. That boolean is fail-closed to
 * `false` while loading and after a failed read, which is right for hiding a
 * create button and wrong for deciding which world someone is looking at — so
 * the resolver still cannot see it, and the separation between "where may I
 * create" and "what am I reading" survives as a property of the signature
 * rather than of anyone's discipline.
 */
export default function useInvoiceSource({ loading, error, provider, reload }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedSource = searchParams.get(INVOICE_SOURCE_PARAM);
  const confirmedProvider = confirmedProviderOf({ loading, error, provider });

  const { source, ignoredRequest } = useMemo(
    () => resolveInvoiceSource({ requestedSource, loading, error, provider }),
    [requestedSource, loading, error, provider]
  );

  const labels = useMemo(
    () => resolveSourceLabels({ source, provider: confirmedProvider }),
    [source, confirmedProvider]
  );

  const setSource = useCallback(
    (next) => {
      const value = normalizeInvoiceSource(next);
      // A value the contract rejects is never written to the URL. Nothing in the
      // product produces one, so this is a guard against a future caller rather
      // than a case anyone can reach today.
      if (value === null) return;
      setSearchParams((prev) => {
        // Copied so every other parameter on the page survives the switch.
        const params = new URLSearchParams(prev);
        params.set(INVOICE_SOURCE_PARAM, value);
        return params;
      });
      // Default push semantics: back and forward step through the worlds the
      // reader actually visited.
    },
    [setSearchParams]
  );

  return {
    source,
    provider: confirmedProvider,
    labels,
    setSource,
    isResolving: loading,
    error,
    retry: reload,
    ignoredRequest,
  };
}
